/**
 * Quote Card
 *
 * Compact card showing quote summary:
 * - Quote number, project name, due date
 * - Progress bar (completed/total jobs)
 * - Badge showing "X jobs need [PA]"
 * - Team activity indicator
 */

export default function QuoteCard({
  quote,
  isSelected,
  onSelect,
  selectedWorkCenters,
}) {
  const progress = quote.totalJobs > 0
    ? Math.round((quote.completedJobs / quote.totalJobs) * 100)
    : 0;

  // Format due date with urgency
  const formatDueDate = (dateStr) => {
    if (!dateStr) return { text: "No date", className: "text-gray-500" };

    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((dateOnly - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: "OVERDUE", className: "text-red-400 font-bold" };
    if (diffDays === 0) return { text: "TODAY", className: "text-green-400 font-bold" };
    if (diffDays === 1) return { text: "TOMORROW", className: "text-yellow-400 font-bold" };
    if (diffDays <= 7) return { text: `${diffDays}d`, className: "text-yellow-600" };

    return {
      text: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      className: "text-gray-500",
    };
  };

  const dueInfo = formatDueDate(quote.dueDate);

  // Get work center labels for badge
  const myWorkCenterLabels = selectedWorkCenters.slice(0, 2).join(", ");

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-3 transition-colors ${
        isSelected
          ? "bg-indigo-600/30 border-l-4 border-indigo-500"
          : "hover:bg-slate-100 border-l-4 border-transparent"
      }`}
    >
      {/* Header: Quote Number + Due Date */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-mono text-gray-500">
          {quote.quoteNumber}
        </span>
        <span className={`text-xs ${dueInfo.className}`}>
          {dueInfo.text}
        </span>
      </div>

      {/* Project Name */}
      <h3 className="text-sm font-semibold text-gray-900 truncate mb-2">
        {quote.projectName || quote.customerCode || "Unnamed Project"}
      </h3>

      {/* Progress Bar */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-gray-500">
          {quote.completedJobs}/{quote.totalJobs}
        </span>
      </div>

      {/* My Work Badge + Team Activity */}
      <div className="flex items-center justify-between">
        {quote.myPendingJobs > 0 ? (
          <span className="text-xs bg-indigo-500/30 text-indigo-300 px-2 py-0.5 rounded">
            {quote.myPendingJobs} job{quote.myPendingJobs !== 1 ? "s" : ""} need{quote.myPendingJobs === 1 ? "s" : ""} {myWorkCenterLabels}
          </span>
        ) : (
          <span className="text-xs text-gray-500">No pending work</span>
        )}

        {/* Team Activity Indicator */}
        {quote.teamActivity && quote.teamActivity.length > 0 && (
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-gray-500">
              {quote.teamActivity.length} active
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
